import type { FC } from 'react';
import Button from '@components/Button';
import { Chip } from '@components/Chip';
import { Show } from '@components/Show';
import { DEFAULT_CREATOR_PADDING } from '@ContractBuilder/constants';
import { sanitizeHtml } from '@helpers/sanitizeHtml';
import { useLanguage } from '@src/language';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, X } from 'lucide-react';

import type { SelectedLibraryBlockSummary } from '../types';

export interface SelectedItemsPanelProps {
  selectedBlocks: Map<string, SelectedLibraryBlockSummary>;
  isClause: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onRemove: (blockId: string) => void;
  onClearSelection: () => void;
  onInsertAll: () => void;
}

const createMarkup = (html: string) => ({ __html: sanitizeHtml(html) });

interface SelectedItemRowProps {
  index: number;
  block: SelectedLibraryBlockSummary;
  isClause: boolean;
  onRemove: (blockId: string) => void;
}

const SelectedItemRow: FC<SelectedItemRowProps> = ({ index, block, isClause, onRemove }) => {
  const { getContent } = useLanguage({ prefix: 'naming.mrc.block.layout' });
  const chipLabel = isClause ? block.clause_type : getContent(block.type);

  return (
    <div
      className={clsx(
        'border-info-200 flex items-center gap-3 border-t bg-white py-4 text-xs',
        DEFAULT_CREATOR_PADDING,
      )}
      data-testid={`selected-items-panel:item:${block.id}`}
    >
      <span className="text-info-400 w-4 shrink-0 text-center">{index}</span>
      <Show when={isClause && !!block.clause_reference}>
        <span className="shrink-0 truncate text-sm font-semibold">{`${block.clause_reference} - `}</span>
      </Show>
      <span
        className="shrink truncate text-sm font-semibold"
        dangerouslySetInnerHTML={createMarkup(block.name ?? block.title ?? block.id.split('-')[0])}
      />
      <Show when={!!chipLabel}>
        <Chip data-testid={chipLabel} dot>
          {chipLabel}
        </Chip>
      </Show>
      <button
        type="button"
        className="text-info-400 hover:text-info-600 ml-auto shrink-0"
        onClick={() => onRemove(block.id)}
        aria-label="Remove from selection"
        data-testid={`selected-items-panel:remove:${block.id}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const SelectedItemsPanel: FC<SelectedItemsPanelProps> = ({
  selectedBlocks,
  isClause,
  isExpanded,
  onToggleExpanded,
  onRemove,
  onClearSelection,
  onInsertAll,
}) => {
  const selectedCount = selectedBlocks.size;
  const isOpen = selectedCount > 0;
  const items = Array.from(selectedBlocks.values());
  const noun = isClause ? 'clause' : 'block';
  const selectionLabel = `${selectedCount} ${noun}${selectedCount === 1 ? '' : 's'} selected`;

  return (
    <div
      data-testid="selected-items-panel"
      className={clsx(
        'shrink-0 overflow-hidden transition-all duration-200 ease-in-out',
        isOpen ? 'max-h-[400px]' : 'max-h-0',
      )}
    >
      <Show when={isOpen}>
        <div>
          <div
            className={clsx('bg-primary-100 flex cursor-pointer items-center gap-2 py-3.5', DEFAULT_CREATOR_PADDING)}
            role="button"
            tabIndex={0}
            onClick={onToggleExpanded}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggleExpanded();
              }
            }}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse selection' : 'Expand selection'}
            data-testid="selected-items-panel:toggle"
          >
            <span className="text-primary-700 shrink-0">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
            <span
              className="text-primary-700 text-sm font-semibold whitespace-nowrap"
              data-testid="selected-items-panel:count"
            >
              {selectionLabel}
            </span>
            <div className="ml-auto flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
              <Button className="h-8 px-2" onClick={onClearSelection} data-testid="selected-items-panel:clear">
                Clear selection
              </Button>
              <Button
                kind="primary"
                className="h-8 px-2"
                onClick={onInsertAll}
                data-testid="selected-items-panel:insert-all"
              >
                Insert all selected
              </Button>
            </div>
          </div>
          <Show when={isExpanded}>
            <div className="max-h-[240px] overflow-y-auto">
              {items.map((block, index) => (
                <SelectedItemRow
                  key={block.id}
                  index={index + 1}
                  block={block}
                  isClause={isClause}
                  onRemove={onRemove}
                />
              ))}
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
};
